import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";
import { FormControl, MenuItem, Select, Typography } from "@mui/material";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { truncDynCell } from "common/Table/truncDynCell";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { selectUserInfo } from "../../features/user/userSlice";

interface Data {
  [key: string]: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface ReportPreviewTableProps {
  columns: any[];
  rows: any[];
  searchText: any;
  reportNumber?: number;
  tableData: any;
  handleChangePage: (pageNo: number) => void;
  handleChangeRows: (size: number) => void;
  pageSize: number;
}

export const ReportPreviewTable: React.FC<ReportPreviewTableProps> = ({
  columns,
  rows,
  searchText,
  reportNumber,
  tableData,
  handleChangePage,
  handleChangeRows,
  pageSize,
}) => {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const loggedInUserData = useSelector(selectUserInfo);
  const currency = loggedInUserData?.business?.currency || " ";
  const reportColumnExclude: Record<number, string[]> = {
    105: ["currency"],
    113: ["businessId"],
    14: ["invoiceId", "clientId"],
    17: ["id", "active", "invoiceId", "attributes", "currency"]
  };
  const currencyColumns = [
    "totalAmount", "taxAmount", "subTotalAmount", "taxableAmount", "grossProfit", "serviceTotalAmount", "itemTotalAmount",
    "packageTotalAmount", "discount", "externalServiceCost", "cogs", "externalCogs", "externalServiceCogs", "cost", "total", "totalPrice",
    "unitCost", "unitPrice", "totalRevenue", "totalCost", "price", "paymentAmount", "credit", "debit", "amount",
    "totalDue", "salesRevenue", "packageRevenue", "serviceRevenue", "partsRevenue", "ticketPrice", "grossProfit",
    "avgCost", "avgSale", "totalSale", "totalDiscount", "itemCost", "creditNote", "balance", "paid", "itemSellingPrice",
    "totalValue", "averageInvoiceValue", "highestInvoiceAmount", "totalSalesAmount", "tax"
  ];

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage1 = (action: string) => {
    if (tableData) {
      if (action === "first") {
        if (tableData?.pageable?.pageNumber > 0) {
          handleChangePage(0);
        }
      } else if (action === "prev") {
        if (tableData.pageable && tableData?.pageable?.pageNumber > 0) {
          handleChangePage(tableData?.pageable?.pageNumber - 1);
        }
      } else if (action === "next") {
        if (!tableData?.last) {
          handleChangePage(tableData?.pageable?.pageNumber + 1);
        }
      } else if (action === "last") {
        if (!tableData?.last) {
          handleChangePage(tableData?.totalPages - 1);
        }
      }
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangeRows(parseInt(event.target.value));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );
  const orderedColumns = React.useMemo(() => {
    if ([1, 5, 14].includes(reportNumber || 0)) {
      const subTotalIndex = columns.findIndex(c => c.id === "subTotalAmount");
      const discountIndex = columns.findIndex(c => c.id === "discount");

      if (subTotalIndex !== -1 && discountIndex !== -1) {
        const newCols = [...columns];
        const [discountColumn] = newCols.splice(discountIndex, 1);
        const updatedSubTotalIndex = newCols.findIndex(c => c.id === "subTotalAmount");
        newCols.splice(updatedSubTotalIndex + 1, 0, discountColumn);
        return newCols;
      }
    }
    if (reportNumber === 15) {
      const stateIndex = columns.findIndex(c => c.id === "state");
      const plateIndex = columns.findIndex(c => c.id === "vehiclePlateNo");
      if (stateIndex !== -1 && plateIndex !== -1) {
        const newCols = [...columns];
        const [plateColumn] = newCols.splice(plateIndex, 1);
        const updatedStateIndex = newCols.findIndex(c => c.id === "state");
        newCols.splice(updatedStateIndex + 1, 0, plateColumn);

        return newCols;
      }
    }
    if (reportNumber === 105) {
      const columnOrder = [
        "poNo", "poDate", "status", "supplierName", "itemCode", "itemName",
        "store", "quantity", "unitPrice", "discount", "tax", "total", "priority", 
        "shippingDate"
      ];
      const colMap = new Map(columns.map((c) => [c.id, c]));
      const ordered = columnOrder
        .map((id) => colMap.get(id))
        .filter((col): col is NonNullable<typeof col> => col != null);
      if (ordered.length > 0) {
        return ordered;
      }
    }
    return columns;
  }, [columns, reportNumber]);
  const filteredColumns = React.useMemo(() => {
    if (!reportNumber) return orderedColumns;

    const excludedCols = reportColumnExclude[reportNumber] || [];

    // Step 1: remove excluded columns (including "attributes")
    const baseColumns = orderedColumns.filter(
      (col) => !excludedCols.includes(col.id as string)
    );

    // Step 2: handle attributes flattening ONLY for report 17 (Credit Note)
    if (reportNumber === 17) {
      const attributeKeys = Array.from(
        new Set(
          rows.flatMap((row) =>
            Object.keys(row.attributes || {})
          )
        )
      );

      const attributeColumns = attributeKeys.map((key) => ({
        id: key,
        label: t(key),
        accessor: (row: any) => row.attributes?.[key] ?? t("N/A")
      }));

      const merged = [...baseColumns, ...attributeColumns];
      // Customer → CN # → CN date → Inv no → Inv date → amount
      const creditNoteColumnOrder = [
        "customerName",
        "customer",
        "creditNoteNumber",
        "creditNoteNo",
        "creditNoteDate",
        "invoiceNo",
        "invoiceNumber",
        "invoiceDate",
        "amount",
      ];
      const colMap = new Map(merged.map((c) => [c.id, c]));
      return [
        ...creditNoteColumnOrder
          .map((id) => colMap.get(id))
          .filter((col): col is NonNullable<typeof col> => col != null),
        ...merged.filter((c) => !creditNoteColumnOrder.includes(c.id as string)),
      ];
    }

    return baseColumns;
  }, [orderedColumns, reportNumber, rows, t]);
  React.useEffect(() => {
    setPage(0);
  }, [searchText]);

  return (
    <Box
      sx={{ width: "100%" }}
      className="reportsPreviewTable grid"
      id="inventorytable"
    >
      <TableContainer sx={{ maxHeight: 445, overflowY: "auto" }}>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={"small"}
          stickyHeader
        >
          <TableHead className="sticky bg-white top-0 z-[9]">
            <TableRow>
              {filteredColumns.map((column) =>
                  reportNumber === 113 && column.id === "supplier" ? (
                    (visibleRows[0]?.supplier as any)?.map(
                      (s: any, i: number) => (
                        <TableCell key={i} align="center">
                          <TableSortLabel>{s.supplierName}</TableSortLabel>
                        </TableCell>
                      )
                    )
                  ) : (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                      padding={column.disablePadding ? "none" : "normal"}
                      sortDirection={orderBy === column.id ? order : false}
                      sx={{ textAlign: "center" }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={(event) => handleRequestSort(event, column.id)}
                        sx={{ width: "max-content" }}
                      >
                        {reportNumber === 12 && column.id === "employeeId"
                          ? t("email")
                          : reportNumber === 105 && column.id === "unitPrice"
                            ? `${t("subTotal")}${currency && currency?.trim()?.length > 0 ? ` (${currency.toUpperCase()})` : ""}`
                            : currencyColumns.includes(column.id)
                              ? `${column.label}${currency && currency?.trim()?.length > 0 ? ` (${currency.toUpperCase()})` : ""}`
                              : column.label}
                        {orderBy === column.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  )
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  className="py-3"
                >
                  <Typography
                    className="font-Saira font-[500] text-[15px] italic"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("noRecordFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                  >
                    {filteredColumns.map((column) =>
                        reportNumber === 113 && column.id === "supplier" ? (
                          (row[column.id] as any)?.map((s: any, i: number) => (
                            <TableCell key={i} align="center">
                              {s.cost}
                            </TableCell>
                          ))
                        ) : (
                          <TableCell
                            key={column.id}
                            align={column.numeric ? "right" : "left"}
                            padding={column.disablePadding ? "none" : "normal"}
                            component={undefined}
                            id={undefined}
                            scope={undefined}
                            sx={{ width: "auto", textAlign: "center" }}
                          >
                            {Array.isArray(row[column.id])
                              ? null
                              : row[column.id] &&
                                  typeof row[column.id] === "string" &&
                                  /^\d{4}-\d{2}-\d{2}T/.test(
                                    row[column.id] as string
                                  )
                                ? (row[column.id] as any)?.split("T")[0]
                                : (row[column.id] === null ||
                                    row[column.id] === "") && column.id !== "shippingDate"
                                  ? t("N/A")
                                  : truncDynCell(
                                      column.accessor ? column.accessor(row) : row[column.id]
                                    )}
                            {reportNumber === 14 && column.id === "lineItems"
                              ? (row[column.id] as any)
                                  ?.map((item: any) => item.itemName)
                                  .filter(Boolean)
                                  .join(", ")
                              : ""}
                            {reportNumber === 301 &&
                            (column.id === "servicesDone" ||
                              column.id === "technicians")
                              ? (row[column.id] as any)?.join("")
                              : ""}
                          </TableCell>
                        )
                      )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className="flex items-center px-[10px] justify-between">
        <span className="hidden-xs mx-[5px] font-Saira font-[400] text-[13px]">
          {t("rowsPerPage")}
        </span>
        <span>
          <FormControl
            className="m-1"
            size="small"
            sx={{
              "& .MuiSelect-select": {
                paddingRight: "0px !important",
                textAlign: "left",
              },
              "& .MuiInputBase-root": {
                border: "1px solid rgba(0, 0, 0, 0.23) !important",
              },
              "& input": { position: "absolute !important" },
            }}
          >
            <Select
              className="font-Saira font-[500] text-[13px] mt-[2px] w-[75px]"
              value={(tableData && tableData?.pageable?.pageSize) || pageSize}
              onChange={(event) =>
                handleChangeRowsPerPage(
                  event as React.ChangeEvent<HTMLInputElement>
                )
              }
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem
                  className="font-Saira font-[500] text-[13px]"
                  key={option}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </span>
        <span style={{ flex: 1 }}></span>
        <span className="mx-[5px] whitespace-nowrap max-w-[300] font-Saira font-[400] text-[13px]">
          {t("page")}
          <strong className="font-Saira mx-[5px] font-[700] text-[14px]">
            {tableData && tableData.pageable
              ? tableData.pageable.pageNumber + 1
              : 1}
          </strong>{" "}
          <span className="visible-responsive"></span>
        </span>
        <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
          {t("outOf")}
          <strong className="mx-[5px] font-Saira font-[700] text-[14px]">
            {tableData && tableData.totalPages ? tableData.totalPages : 1}
          </strong>
        </span>
        <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
          {t("total")}
          <strong className="mx-[5px] font-Saira font-[700] text-[14px]">
            {tableData && tableData.totalElements ? tableData.totalElements : 0}
          </strong>
        </span>
        <span className="mr-2">
          <FirstPageIcon
            fontSize="small"
            onClick={() => handleChangePage1("first")}
            style={{
              color:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.first
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.first
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <NavigateBeforeIcon
            fontSize="small"
            onClick={() => handleChangePage1("prev")}
            style={{
              color:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.first
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.first
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <NavigateNextIcon
            fontSize="small"
            onClick={() => handleChangePage1("next")}
            style={{
              color:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.last
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.last
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
        <span className="mr-2">
          <LastPageIcon
            fontSize="small"
            onClick={() => handleChangePage1("last")}
            style={{
              color:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.last
                  ? "#1F2020"
                  : "#B8C0C6",
              cursor:
                tableData?.content?.length &&
                tableData?.content?.length !== 0 &&
                !tableData.last
                  ? "pointer"
                  : "text",
            }}
          />
        </span>
      </Box>
    </Box>
  );
};

const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 200];
